import React from 'react';
import {
    Button,
    Modal,
    Text,
    TextAs,
    TextSizes,
    ButtonHierarchies,
} from '@clockout-dev/design-elements';
import LoadingSpinner from '../../../common/LoadingSpinner';
import { formatFunnelDetail, formatFunnelDuration, formatFunnelTimestamp } from '../userFunnel.constants';
import styles from './styles/user-funnel-card.module.scss';

/**
 * Every payroll-link failure behind the timeline's "Payroll connection error"
 * row, from `GET /api/admin/users/:aba/:client_id/link-attempts`.
 *
 * The row itself is a summary — dated from the FIRST failure, worded from the
 * LAST, with a count of the ones between — because a timeline should answer
 * "how long until this broke" and "what is blocking them now". It cannot
 * answer "what did they actually try", which is what this list is for.
 *
 * The endpoint is bounded by the same window that produced the row's count,
 * so the two can never disagree.
 *
 * Each attempt carries the fields the timeline deliberately drops: `source`
 * (which emitter reported it) and `stage` (which screen it broke on). They are
 * noise on a row an agent scans past and the whole point of one they opened.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Array<Object> | null} [props.attempts]
 * @param {boolean} [props.hasError] The endpoint's own `has_error`. Sent so an
 *   empty list can be told apart from a member who never failed — this modal
 *   only opens from an error row, so the two are different sentences.
 * @param {boolean} [props.loading]
 * @param {string | null} [props.error]
 * @param {(() => void) | null} [props.onRetry] Absent = a retry cannot help.
 * @param {() => void} props.onClose
 */
function UserFunnelAttemptsModal({
    open,
    attempts,
    hasError = false,
    loading = false,
    error = null,
    onRetry = null,
    onClose,
}) {
    const rows = Array.isArray(attempts) ? attempts : [];
    // Elapsed since the FIRST attempt, which is what the row is dated from —
    // so an agent reading "+2d" here and the row's own timestamp are talking
    // about the same clock.
    const firstMs = rows.length ? rows[0].at_ms : null;

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            title="Payroll connection attempts"
            destroyOnHidden
            width={640}
        >
            {loading ? <LoadingSpinner size="small" text="Loading this member's attempts…" inline /> : null}

            {!loading && error ? (
                <div className={styles.state}>
                    <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.stateText}>
                        {error}
                    </Text>
                    {onRetry ? (
                        <Button
                            hierarchy={ButtonHierarchies.SECONDARY}
                            onClick={onRetry}
                        >
                            Try again
                        </Button>
                    ) : null}
                </div>
            ) : null}

            {!loading && !error && !rows.length ? (
                <div className={styles.state}>
                    <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.stateText}>
                        {hasError
                            ? // The row this modal opened from says the member DID
                              // fail, so "no attempts" here is a gap in the history,
                              // not an answer about the member.
                              'This member hit a payroll connection error, but none of the individual attempts could be recovered.'
                            : 'No failed attempts recorded during this member\u2019s onboarding.'}
                    </Text>
                </div>
            ) : null}

            {!loading && !error && rows.length ? (
                <ol className={styles.attempts}>
                    {rows.map((attempt, index) => {
                        const since = index === 0 ? null : formatFunnelDuration(attempt.at_ms - firstMs);
                        // Through the same formatter as the timeline's chips, so
                        // `code` cannot be "Error code" here and "Code" on the row
                        // an agent opened this from. Spelled out field by field
                        // rather than handing over the whole attempt: `at_ms` and
                        // `message` are already rendered above, and this order —
                        // where it broke, then why, then who reported it — is the
                        // reading order the modal exists for.
                        const chips = formatFunnelDetail({
                            stage: attempt.stage,
                            code: attempt.code,
                            error_type: attempt.error_type,
                            provider: attempt.provider,
                            source: attempt.source,
                        });

                        return (
                            <li key={`${attempt.at_ms}-${index}`} className={styles.attempt}>
                                <div className={styles.headline}>
                                    <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.title}>
                                        {`Attempt ${index + 1}`}
                                    </Text>
                                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.timestamp}>
                                        {formatFunnelTimestamp(attempt.at_ms) || '—'}
                                    </Text>
                                </div>

                                <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.description}>
                                    {attempt.message || 'No error detail was reported.'}
                                </Text>

                                {since || chips.length ? (
                                    <div className={styles.meta}>
                                        {since ? (
                                            <Text
                                                as={TextAs.SPAN}
                                                size={TextSizes.EXTRA_SMALL}
                                                className={styles.elapsed}
                                            >
                                                {`+${since} after the first`}
                                            </Text>
                                        ) : null}
                                        {chips.map((chip) => (
                                            <Text
                                                key={chip.key}
                                                as={TextAs.SPAN}
                                                size={TextSizes.EXTRA_SMALL}
                                                className={styles.chip}
                                            >
                                                {`${chip.label}: ${chip.value}`}
                                            </Text>
                                        ))}
                                    </div>
                                ) : null}
                            </li>
                        );
                    })}
                </ol>
            ) : null}
        </Modal>
    );
}

export default UserFunnelAttemptsModal;
